import { Component, Inject, Input, Output, EventEmitter, OnInit, SimpleChanges } from '@angular/core'
import { Router, ActivatedRoute } from '@angular/router'
import { SubscriptionActions } from '../../actions/subscription-actions'
import { Customer, Account, Plan, PlanAddOn, Subscription, Coupon, BillingFrequency } from '../../types'
import { discountedCents, availableCouponApplications, prettyDollars, prettyCents } from '../../logic/pricing'
import { BaseComponent } from '../../components/base.component'
import {
  isEmpty,
  get,
  ceil,
  filter,
  sortBy,
  uniqBy,
  pick,
  values,
  capitalize,
  max,
} from 'lodash-es'
import { decodeUrlParam, paymentFailedToReactivate, planChangeSummaryPath } from '../../utils'
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed'

// Animations
import {
  trigger,
  state,
  style,
  animate,
  transition
} from '@angular/animations'
import { contentTypesLearnMoreHtml, addonLearnMoreHtml } from '../../content/pricing-content'
import { MediaQueryService } from '../../services/media-query-service'

const _horizontalScrolling = (pref: 'lock' | 'unlock') => {
  const selectors = [
    '.detail-content',
    'mat-sidenav-content'
  ]

  const setting = pref === 'lock' ? 'hidden' : 'inherit'
  if (document) {
    const elements = selectors.map(selector => <HTMLElement>document.querySelector(selector))
    elements.forEach(el => {if (el) el.style['overflow-x'] = setting})
  }
}

@Component({
  styles: [`
    :host {
      display: block;
      height: 100%;
      width: 100%;
    }
    .agency-buffer {
      margin-bottom: 70px;
    }
    .custom-overflow {
      width: 100%;
      overflow: hidden;
    }
  `],
  animations: [
    trigger('leftVisibility', [
      state('false', style({transform: 'translateX(-200%)', height: '0'})),
      state('true', style({transform: 'translateX(0)', height: '*'})),
      transition('1 => 0', animate('300ms ease')),
      transition('0 => 1', animate('300ms ease'))
    ]),
    trigger('rightVisibility', [
      state('false', style({transform: 'translateX(200%)', height: '0'})),
      state('true', style({transform: 'translateX(0)', height: '*'})),
      transition('1 => 0', animate('300ms ease')),
      transition('0 => 1', animate('300ms ease'))
    ])
  ],
  selector: 'plan-form',
   template: `
      <video-info-modal *ngIf="videoModalVisible" (close)="videoModalVisible = false" [customer]="customer"></video-info-modal>
      <div class="container-fluid plan-form mt-1 custom-overflow" *ngIf="componentReady()">
        <div class="text-center">
          <div class="row">
            <div class="col text-center">
              <h3 *ngIf="www">Build your monthly plan</h3>
              <h4 class="mt-3 mb-2 lato font-weight-light text-no-transform">
                1) Choose a word length.
              </h4>
              <p class="mb-3">
                If you need content at different lengths, you can add multiple subscriptions to your Verblio account.
              </p>
            </div>
          </div>
          <div class="row" class="my-2" *ngIf="['active', 'paused'].includes(customer?.state) && onRecentOldPlan()">
            <div class="col">
              <p class="alert alert-info">
                You’re currently on a legacy plan from before January 2nd, 2020. We raised our prices at that time. However, because you are a valued customer, your 2019 prices are locked in until March 31st, even if you make changes to your plan.
                <span *ngIf="billingFrequency != 'annually'">You can extend 2019 pricing and get an additional 17% discount for a full 12 months by switching to an annual plan at the bottom of this page before March 31st.</span>
                Please contact support@verblio.com if you have any questions.</p>
            </div>
          </div>

          <!-- Step 1 - Post Length -->
          <div class="text-center">

            <div [@leftVisibility]="!customLength">
              <ng-template #popupEliteWriters><div class="text-black" [innerHtml]="eliteWritersPopover"></div></ng-template>
              <ng-template #popupFullMarketplace><div class="text-black" [innerHtml]="fullMarketplacePopover"></div></ng-template>
              <div class="row">
                <ng-template ngFor let-p let-i="index" [ngForOf]="monthlyCorePlans(plans)">
                  <div class="col-stack-mobile px-2">
                    <div [class.selected]="words === p.words" class="pricing-box pricing-box-words my-2 cursor-pointer" (click)="updateWords(p.words)">
                      <h4>{{p.words.toLocaleString()}} Words</h4>
                      <div class="small" style="white-space: pre-wrap">{{corePlanWordsSummaries[p.words]}}</div>
                      <p class="price words"><span class="currency">$</span>
                      <span class="dollars">{{dollars(p.price)}}</span>
                      <span class="cents">{{cents(p.price)}}</span>
                      <span class="per-month">/piece</span></p>

                      <div class="mt-2">
                        <ng-template #contentTypesPopover>
                          <div [innerHtml]="contentTypesPopoverHtml(p.words) | keepHtml"></div>
                        </ng-template>
                        <a class="link cursor-pointer"
                          [popover]="contentTypesPopover"
                          container="body"
                          [placement]="popoverPlacement(i, monthlyCorePlans(plans).length)"
                          triggers="mouseenter:mouseleave">
                          <i class="fas fa-question-circle text-tertiary" style="line-height: inherit;"></i>
                        </a>
                      </div>
                    </div>
                  </div>
                </ng-template>
              </div>
              <div class="d-flex flex-row row mt-3">
                <div class="px-2" style="flex: 0 0 40%">
                  <div class="col-sm px-0 d-none d-md-block">
                    <div class="elite-writers">
                      <div class="text">
                        Access to full marketplace
                        <i class="ml-1 fas fa-question-circle text-tertiary" [popover]="popupFullMarketplace" triggers="mouseenter:mouseleave"></i>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="px-2" style="flex: 0 0 60%">
                  <div class="col-sm px-0 d-none d-md-block">
                    <div class="elite-writers">
                      <div class="text">
                        Elite writers only
                        <i class="ml-1 fas fa-question-circle text-tertiary" [popover]="popupEliteWriters" triggers="mouseenter:mouseleave"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="mt-2">
                <div>Or choose a <a class="link cursor-pointer" (click)="customLength = true">custom length</a></div>
              </div>
            </div>
            <div [@rightVisibility]="customLength">
              <p>Select a custom word length or <a routerLink="." (click)="customLength = false">choose one of the defaults</a></p>
              <plan-page-picker [type]="'words'" [value]="words" (valueChange)="updateWords($event)"></plan-page-picker>
              <p class="font-size-12 my-0 reverse-mt-2">words</p>
              <p class="price">
                <span class="currency">$</span>
                <span class="dollars">{{dollars(pricePerPieceWithoutAddons({overrideBillingFrequency: 'monthly'}))}}</span>
                <span class="cents">{{cents(pricePerPieceWithoutAddons({overrideBillingFrequency: 'monthly'}))}}</span>
              </p>
            </div>
          </div>

          <div class="my-3" *ngIf="!stepComplete(Steps.WORDS)">
            <button class="btn btn-secondary big-text horizontal-padding" (click)="completeStep(Steps.WORDS)">Next</button>
          </div>


          <!-- Step 2 - Quantity -->
          <div id="step-{{Steps.QUANTITY}}" *ngIf="stepComplete(Steps.WORDS)">
            <div *ngIf="!stepComplete(Steps.QUANTITY)" [class.agency-buffer]="agencySignup()"></div>
            <div class="row my-4">
              <div class="col-12">
                <h4 class="mb-4 lato font-weight-light text-no-transform">2) Choose how many {{words.toLocaleString()}}-word pieces you want per month.<span *ngIf="(words===300 || words===600) && !www">*</span></h4>
                <p *ngIf="account && !account?.agency && (words === 300 || words === 600)">*the {{words.toLocaleString()}}-word length has a minimum of {{minNumberOfPosts[words]}} pieces per month</p>
                <plan-page-picker [type]="'numberOfPosts'" [value]="quantity" (valueChange)="onQuantityChange($event)" [invalid]="!!quantityErrorMessage"></plan-page-picker>

                <div *ngIf="!quantityErrorMessage" class="price">
                  <span class="currency">Subtotal: $</span>
                  <span class="dollars">{{dollars(monthlyPriceWithoutAddOns({overrideBillingFrequency: 'monthly'}))}}</span>
                  <span class="cents">{{cents(monthlyPriceWithoutAddOns({overrideBillingFrequency: 'monthly'}))}}</span>
                  <span class="per-month">per month</span>
                </div>
              </div>
            </div>

            <div class="my-4">
              <div *ngIf="!!quantityErrorMessage" class="text-danger">
                {{quantityErrorMessage}}
              </div>
              <button
                *ngIf="!stepComplete(Steps.QUANTITY) && !quantityErrorMessage"
                class="btn btn-secondary big-text horizontal-padding"
                [disabled]="!!quantityErrorMessage"
                (click)="completeStep(Steps.QUANTITY)">Next</button>
            </div>
            <div *ngIf="!stepComplete(Steps.QUANTITY)" style="height: 300px;"></div>
          </div>


          <!-- Step 3 - Package -->
          <div *ngIf="!stepComplete(Steps.PACKAGE)" [class.agency-buffer]="agencySignup()"></div>
          <div id="step-{{Steps.PACKAGE}}" *ngIf="stepComplete(Steps.QUANTITY)" [class.mb-3]="!stepComplete(Steps.PACKAGE)">
            <div class="row mt-4">
              <div class="col">
                <h4 class="lato font-weight-light text-no-transform">3) Add optional services.</h4>
                <p class="mt-2 mb-4 w-80 mx-auto">
                The below services are ongoing and apply to all pieces of content in the subscription, i.e. if you receive 3 pieces of content a month and sign up for +Photo, you'll receive +Photo on all 3 of those pieces.
                </p>
                <div class="row justify-content-around">
                  <div class="d-none d-md-block col px-2 text-right font-size-12 package-features">
                    <div class="my-2 feature"><span>{{ addonFeatureDescription(0) }}</span></div>
                    <div class="my-2 feature"><span>{{ addonFeatureDescription(1) }}</span></div>
                    <div class="my-2 feature"><span>{{ addonFeatureDescription(2) }}</span></div>
                    <div class="my-2 feature"><span>{{ addonFeatureDescription(3) }}</span></div>
                    <div class="my-2 feature"><span>{{ addonFeatureDescription(4) }}</span></div>
                    <div class="my-2 feature"><span>{{ addonFeatureDescription(5) }}</span></div>
                  </div>
                  <div class="col-stack-mobile px-2">
                    <div class="pricing-box" [class.selected]="basic" (click)="selectPackage('basic', true, false)">
                      <div class="my-2">
                        <h5>Basic</h5>
                      </div>
                      <div class="my-2 feature-description-mobile">
                        <i class="fas fa-check-circle no-mobile"></i>
                        <div class="description-mobile">{{addonFeatureDescription(0)}}</div>
                      </div>
                      <div class="my-2 no-mobile"><i class="fas fa-minus"></i></div>
                      <div class="my-2 no-mobile "><i class="fas fa-minus"></i></div>
                      <div class="my-2 no-mobile"><i class="fas fa-minus"></i></div>
                      <div class="my-2 no-mobile"><i class="fas fa-minus"></i></div>
                      <div class="my-2 feature">Included!</div>
                    </div>
                    <div class="mt-2">
                      <ng-template #addonBasicPopover><div [innerHtml]="addonPopoverHtml('basic') | keepHtml"></div></ng-template>
                      <a class="link cursor-pointer"
                        [popover]="addonBasicPopover"
                        container="body"
                        triggers="mouseenter:mouseleave"
                        [placement]="popoverPlacement(0, 4)">Learn more</a>
                    </div>
                  </div>
                  <div class="col-stack-mobile px-2">
                    <div [class.selected]="plusPhoto" class="pricing-box" (click)="selectPackage('plusPhoto', true, false)">
                      <div class="my-2">
                        <h5>+Photo</h5>
                      </div>
                      <div class="my-2 feature-description-mobile">
                        <i class="fas fa-check-circle no-mobile"></i>
                        <div class="description-mobile">{{ addonFeatureDescription(0) }}</div>
                      </div>
                      <div class="my-2 feature-description-mobile">
                        <i class="fas fa-check-circle no-mobile"></i>
                        <div class="description-mobile">{{ addonFeatureDescription(1) }}</div>
                      </div>
                      <div class="my-2 no-mobile"><i class="fas fa-minus"></i></div>
                      <div class="my-2 no-mobile"><i class="fas fa-minus"></i></div>
                      <div class="my-2 no-mobile"><i class="fas fa-minus"></i></div>
                      <div class="my-2 feature">
                        {{'+$'+dollars(perPiecePriceForAddon('plusPhoto'))}}.{{cents(perPiecePriceForAddon('plusPhoto'))}}
                      </div>
                    </div>
                    <div class="mt-2">
                      <ng-template #addonPhotoPopover><div [innerHtml]="addonPopoverHtml('plusPhoto') | keepHtml"></div></ng-template>
                      <a class="link cursor-pointer"
                        [popover]="addonPhotoPopover"
                        container="body"
                        triggers="mouseenter:mouseleave"
                        [placement]="popoverPlacement(1, 4)">Learn more</a>
                    </div>
                  </div>
                  <div class="col-stack-mobile px-2">
                    <div [class.selected]="plusOptimize" class="pricing-box" (click)="selectPackage('plusOptimize', true, false)">
                      <div class="my-2">
                        <h5>+Optimize*</h5>
                      </div>
                      <div class="my-2 feature-description-mobile">
                        <i class="fas fa-check-circle no-mobile"></i>
                        <div class="description-mobile">{{ addonFeatureDescription(0) }}</div>
                      </div>
                      <div class="my-2 feature-description-mobile">
                        <i class="fas fa-check-circle no-mobile"></i>
                        <div class="description-mobile">{{ addonFeatureDescription(1) }}</div>
                      </div>
                      <div class="my-2 feature-description-mobile">
                        <i class="fas fa-check-circle no-mobile"></i>
                        <div class="description-mobile">{{ addonFeatureDescription(2) }}</div>
                      </div>
                      <div class="my-2 feature-description-mobile">
                        <i class="fas fa-check-circle no-mobile"></i>
                        <div class="description-mobile">{{ addonFeatureDescription(3) }}</div>
                      </div>
                      <div class="my-2 no-mobile"><i class="fas fa-minus"></i></div>
                      <div class="my-2 feature">
                        {{'+$'+dollars(perPiecePriceForAddon('plusOptimize'))}}.{{cents(perPiecePriceForAddon('plusOptimize'))}}
                      </div>
                    </div>
                    <div class="mt-2">
                      <ng-template #addonOptimizePopover><div [innerHtml]="addonPopoverHtml('plusOptimize') | keepHtml"></div></ng-template>
                      <a class="link cursor-pointer"
                        [popover]="addonOptimizePopover"
                        container="body"
                        triggers="mouseenter:mouseleave"
                        [placement]="popoverPlacement(2, 4)">Learn more</a>
                    </div>
                  </div>
                  <div class="col-stack-mobile px-2">
                    <div [class.selected]="verblioComplete" class="pricing-box" (click)="selectPackage('verblioComplete', true, false)">
                      <div class="my-2">
                        <h5>Verblio Complete</h5>
                      </div>
                      <div class="my-2 feature-description-mobile">
                        <i class="fas fa-check-circle no-mobile"></i>
                        <div class="description-mobile">{{ addonFeatureDescription(0) }}</div>
                      </div>
                      <div class="my-2 feature-description-mobile">
                        <i class="fas fa-check-circle no-mobile"></i>
                        <div class="description-mobile">{{ addonFeatureDescription(1) }}</div>
                      </div>
                      <div class="my-2 feature-description-mobile">
                        <i class="fas fa-check-circle no-mobile"></i>
                        <div class="description-mobile">{{ addonFeatureDescription(2) }}</div>
                      </div>
                      <div class="my-2 feature-description-mobile">
                        <i class="fas fa-check-circle no-mobile"></i>
                        <div class="description-mobile">{{ addonFeatureDescription(3) }}</div>
                      </div>
                      <div class="my-2 feature-description-mobile">
                        <i class="fas fa-check-circle no-mobile"></i>
                        <div class="description-mobile">{{ addonFeatureDescription(4) }}</div>
                      </div>
                      <div class="my-2 feature">
                        {{'+$'+dollars(perPiecePriceForAddon('verblioComplete'))}}.{{cents(perPiecePriceForAddon('verblioComplete'))}}
                      </div>
                    </div>
                    <div class="mt-2">
                      <ng-template #addonCompletePopover><div [innerHtml]="addonPopoverHtml('verblioComplete') | keepHtml"></div></ng-template>
                      <a class="link cursor-pointer"
                        [popover]="addonCompletePopover"
                        container="body"
                        triggers="mouseenter:mouseleave"
                        [placement]="popoverPlacement(3, 4)">Learn more</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="row my-3">
              <div class="col">
                <em class="bg-hover-blue">*+Optimize works best with blog posts and some web content</em>
              </div>
            </div>
            <div class="row my-3">
              <div class="col">
                <div class="pricing-box p-3 text-left" [class.selected]="plusVideo" (click)="toggleAddOn('plusVideo')">
                  <div class="d-flex flex-row align-items-center justify-content-between">
                    <div class="d-flex flex-row align-items-center justify-content-start">
                      <div class="font-size-24" [ngClass]="{'far fa-square': !plusVideo, 'fas fa-check-square': plusVideo}"></div>
                      <div class="ml-3">
                        <div>
                          <h4 class="d-inline">+Video</h4>
                          <h6 class="d-inline highlight ml-2">NEW!</h6>
                        </div>
                        <div>
                          Hook and expand your audience by adding a 30-60 second teaser or summary video to every piece of content you purchase.
                        </div>
                      </div>
                    </div>
                    <ng-template #plusVideoPopover>
                      You
                      <strong>
                        save {{ videoSavings }} on each video
                      </strong>
                      by being a video subscriber!
                    </ng-template>
                    <div
                      [popover]="plusVideoPopover"
                      container="body"
                      triggers="mouseenter:mouseleave"
                      class="d-flex flex-column justify-content-center align-items-center">
                      <div class="price font-size-20 text-secondary">
                        <div class="currency">$</div>
                        <div class="dollars">{{dollars(perPiecePriceForAddon('plusVideo'))}}</div>
                        <div class="cents">{{cents(perPiecePriceForAddon('plusVideo'))}}</div>
                      </div>
                      <div class="strikeout price font-size-20">
                        <div class="currency">$</div>
                        <div class="dollars">{{dollars(perPiecePriceForAddon('plusVideo', {useExtraPrice: true}))}}</div>
                        <div class="cents">{{cents(perPiecePriceForAddon('plusVideo', {useExtraPrice: true}))}}</div>
                      </div>
                      <div class="small" [style.white-space]="'nowrap'">
                        per video
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <button class="btn btn-link" (click)="videoModalVisible = true">Learn more and see a sample video</button>
              </div>
            </div>
            <div class="my-3" *ngIf="!stepComplete(Steps.PACKAGE)">
              <button class="btn btn-secondary big-text horizontal-padding" (click)="completeStep(Steps.PACKAGE)">Next</button>
            </div>
          </div>


          <!-- Step 4 - Billing Frequency -->
          <div id="step-{{Steps.BILLING_FREQUENCY}}" *ngIf="stepComplete(Steps.PACKAGE)">
            <div class="row mt-4">
              <div class="col">
                <h4 class="mb-2 lato font-weight-light text-no-transform">4) Choose if you want to pay monthly, or annually. </h4>
                <p>Get a discount for paying annually—12 months for the price of 10!</p>
              </div>
            </div>
            <div class="row mt-4">
              <div class="col-md-3 offset-md-3 mb-3 billing-frequency">
                <div (click)="billingFrequency = 'monthly'" class="cursor-pointer pricing-box small-vertical-padding text-center" [class.selected]="billingFrequency == 'monthly'">
                  <h5 class="mb-1 mt-0"><b>Monthly</b></h5>
                  <p class="price my-0">
                    <span class="currency">$</span>
                    <span class="dollars">{{dollars(monthlyPriceWithAddOns({overrideBillingFrequency: 'monthly'}))}}</span>
                    <span class="cents">{{cents(monthlyPriceWithAddOns({overrideBillingFrequency: 'monthly'}))}}</span>
                  </p>
                  <p class="font-size-12">per month<br>billed monthly</p>
                </div>
              </div>
              <div class="col-md-3 billing-frequency">
                <div (click)="billingFrequency = 'annually'" class="cursor-pointer pricing-box small-vertical-padding text-center" [class.selected]="billingFrequency == 'annually'">
                  <div class="annual-tag">
                    <div class="height-adjust">
                      <div class="small">Save</div>
                      <div class="annual-tag-price">
                          <span class="currency">$</span>
                          <span class="dollars">{{dollars(annualSavings())}}</span>
                      </div>
                      <div class="small per-month">per year</div>
                    </div>
                  </div>
                  <h5 class="mb-1 mt-0"><b>Annually</b></h5>
                  <p class="price my-0">
                    <span class="currency">$</span>
                    <span class="dollars">{{dollars(monthlyPriceWithAddOns({overrideBillingFrequency: 'annually'}))}}</span>
                    <span class="cents">{{cents(monthlyPriceWithAddOns({overrideBillingFrequency: 'annually'}))}}</span>
                  </p>
                  <p class="font-size-12">per month<br>billed annually</p>
                </div>
              </div>
            </div>

            <div class="row mt-4">
              <div class="col">
                <h3 class="text-center">Summary</h3>
              </div>
            </div>
            <div class="row">
              <div class="col text-right">
                <div>Length:</div>
                <div>Frequency:</div>
                <div>Package:</div>
                <div class="mt-1">{{ billingFrequency === 'annually' ? 'Annual' : 'Monthly' }} total:</div>
              </div>
              <div class="col text-left">
                <div class="">{{words.toLocaleString()}} words per piece</div>
                <div class="">{{this.quantity}} pieces per month</div>
                <div class="">{{planName()}}</div>
                <div class="text-left xs-text-center price text-inherit">
                  <span class="currency">$</span>
                  <span class="dollars">{{dollars(finalPriceCents())}}</span>
                  <span class="cents">{{cents(finalPriceCents())}}</span>
                  <span class="font-weight-normal" style="font-size: 55%" *ngIf="hasCoupons() && billingFrequency === 'monthly'">*</span>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <!-- COUPONS -->
                <div *ngIf="hasCoupons() && billingFrequency === 'monthly'" class="discount-text text-center">
                  <div *ngFor="let c of usableCoupons()">
                    <span>
                    * Discount applied: {{c.externalName}}
                    </span>
                  </div>
                  Final price shown with discount applied. Cents vary due to rounding.
                </div>
              </div>
            </div>

            <div class="row my-3" *ngIf="!www">
              <div class="col-12">
                <button
                  type="submit"
                  class="btn btn-lg btn-primary horizontal-padding"
                  [disabled]="saving || noPlanInfo() || !!quantityErrorMessage"
                  (click)="onSubmit()">
                  {{createPlanText()}}
                </button>
              </div>
              <div class="col-12">
                <span *ngIf="customer?.state === 'active'" class="small">You will not be charged until the next step.</span>
              </div>
            </div>

            <div class="row my-4" *ngIf="www">
              <div class="col-md-12">
                <h3 class="my-4">Ready to go?</h3>
                <h4 class="my-4 lato font-weight-light text-no-transform">5) Sign up with this plan.</h4>
                <form class="row my-2" (submit)="onSubmitWww()">
                  <div class="col-sm-2 offset-sm-4 form-group text-right pr-0">
                    <input name="email" type="email" class="form-control" (input)="email = $event.target.value" placeholder="Enter your email" />
                  </div>
                  <div class="col-sm-2 form-group text-left pl-0 ml-2">
                    <button type="submit" class="btn btn-primary">Save + Continue</button>
                  </div>
                </form>
              </div>
            </div>

            <div *ngIf="['active', 'paused'].includes(customer?.state)" class="row my-4 text-center">
              <div class="col-md-12">
                <a id="cancel-subscription" href="/customers/{{customer?.id}}/subscription"><strong>Cancel my subscription</strong></a>
              </div>
            </div>
          </div>
        </div>
      </div>
  `
})
export class PlanFormComponent extends BaseComponent implements OnInit {
  @Input() account: Account;
  @Input() customer: Customer;
  @Input() plans: Plan[] = [];
  @Input() newCustomer: boolean = false;
  @Input() steps: boolean = true;
  @Input() adminImpersonating: boolean = false;
  @Output() onCreate: EventEmitter<boolean> = new EventEmitter();
  @Output() onSubmitwww: EventEmitter<string> = new EventEmitter();
  quantity: number = 4;
  quantityErrorMessage: string = null
  words: number = 1000;
  recommended: number = 1000;
  billingFrequency: BillingFrequency = 'monthly';
  basic: boolean = false;
  plusPhoto: boolean = false;
  plusOptimize: boolean = true;
  verblioComplete: boolean = false;
  plusVideo: boolean = false;
  email: string;
  saving: boolean = false;
  paramsAddOn: AddOnDescriptor = undefined;
  customLength: boolean = false;
  videoModalVisible: boolean = false
  eliteWritersPopover: string = `Elite writers are the <b>top 10% of accepted writers</b> in our system. They've been personally vetted, sold at least 75 pieces, and are most popular with customers.`;
  fullMarketplacePopover: string = `We curate our marketplace with a focus on quality upfront. Only the <b>top 5% of writers who apply make it in</b>, and we continually QA to keep standards high.`;
  /* If we reorder in the dom, we shouldn't need to actually change these numbers,
  they are acting as enums */
  Steps = {
    WORDS: 1,
    QUANTITY: 2,
    PACKAGE: 3,
    BILLING_FREQUENCY: 4
  };

  completedSteps = {
    [this.Steps.WORDS]: false,
    [this.Steps.QUANTITY]: false,
    [this.Steps.PACKAGE]: false
  };

  defaultQuantities = {
    300: 8,
    600: 8,
    1000: 4,
    1500: 4,
    2000: 2
  };

  defaultWordLengths: number[] = Object.keys(this.defaultQuantities).map(k => Number.parseInt(k))

  constructor(
    @Inject(SubscriptionActions) private subscriptionActions: SubscriptionActions,
    private route: ActivatedRoute,
    private router: Router,
    private mediaQuery: MediaQueryService
    ) { super() }

  get isMobile(): boolean {
    return this.mediaQuery.isUnder('lg')
  }

  corePlanWordsSummaries = {
    300: 'Micro Blog Posts\nWebsite Content\n\n\n',
    600: 'Short Blog Posts\nNewsletters\nWebsite Content\n\n',
    1000: 'Blog Posts\nEbook Chapters\nWebsite Content\n\n',
    1500: 'In-Depth Articles\nEbook Chapters\nWhite Papers\nWebsite Content',
    2000: 'Thought Leadership\nEbook Chapters\nWhite Papers\nWebsite Content'
  };

  corePlanWords = Object.keys(this.corePlanWordsSummaries).map(k => +k);

  notAvailableTypes = {
    300: 'Ebook chapters\nWhite Papers',
    600: 'Ebook chapters\nWhite Papers',
    1000: 'Newsletter',
    1500: 'Newsletters\nPress Releases',
    2000: 'Newsletters\nPress Releases'
  };

  addonFeatureDescription(index: number): string {
    const descriptions = [
      'Content written on requested topics',
      `${capitalize(this.photosPerPostInWords())} hand-selected stock photos per piece`,
      'Back-end SEO optimization',
      '2x Proofreading; CMS formatting and publishing',
      'Dedicated account manager',
      'Price per piece'
    ]
    return descriptions[index]
  }

  monthlyCorePlans(plans: Plan[]): Plan[] {
    const currentPlanActive = get(this.customer, 'subscription.plan.active')
    const subscriptionState = get(this.customer, 'subscription.state')
    let specialFilter

    if (['active', 'paused'].includes(subscriptionState) && !currentPlanActive) {
      specialFilter = p => (p.id >= 42 && p.id <= 71) || (p.id >= 112 && p.id <= 151)
    } else {
      specialFilter = p => p.billingFrequency === 'monthly' && p.active === true
    }

    const monthlyCurrentOnly = filter(plans, specialFilter)
    const core = filter(monthlyCurrentOnly, p => this.corePlanWords.includes(p.words))

    const uniq = uniqBy(core, 'words')
    const sorted = sortBy(uniq, 'words')
    return sorted
  }

  ngOnInit(): void {
    const routeObs = this.route.queryParams.subscribe(queryParams => {
      // We may want to update this to allow multiple addOns.
      this.paramsAddOn = decodeUrlParam(queryParams['addOn']) as AddOnDescriptor
      if (this.paramsAddOn) this.selectPackage(this.paramsAddOn)
      this.email = decodeUrlParam(queryParams['email'])
    })
    this.observers.push(routeObs)

    if (this.customer && !this.customer.subscription) {
      this.loadPreviousSelections()
    }

    _horizontalScrolling('lock')
  }

  ngOnDestroy(): void {
    _horizontalScrolling('unlock')
  }

  componentReady(): boolean {
    return this.plans.length > 0
  }

  /* Submission */
  onSubmit(): void {
    if (this.quantityErrorMessage) return // Handle someone manually removing disabled class
    if (['active', 'paused'].includes(this.customer.state)) {
      const url = planChangeSummaryPath({
        customer: this.customer,
        planCode: this.selectedPlan().code,
        options: this.selectedAddOnCodes(),
        quantity: this.quantity
      })
      window.location.assign(url)
    } else {
      this.saving = true
      /* Create a subscription - for active or pending customers */
      this.subscriptionActions.createSubscription(this.customer.id, this.selectedPlan().code, this.selectedAddOnCodes(), this.quantity)
      .then(() => {
        this.saving = false
        this.onCreate.emit(true)
      })
      .catch(err => {
        this.saving = false
        if (paymentFailedToReactivate(err)) {
          this.router.navigate(['/dashboard', 'billing_info', 'edit'], { queryParams: {
            activate: true,
            customerId: this.customer.id,
            planCode: this.selectedPlan().code,
            options: this.selectedAddOnCodes().join(','),
            quantity: this.quantity
          }})
        }
      })
    }
  }

  onSubmitWww(): void {
    this.saveSelections()
    this.onSubmitwww.emit(this.email)
  }

  onQuantityChange(newQuantity: number): void {
    this.quantity = newQuantity
    this.quantityErrorMessage = this.getQuantityErrorMessage(this.quantity, this.words, this.account)
  }

  stepComplete(step: number): boolean {
    if (!this.steps) return true
    if (step <= 2 && this.account && this.account.fullySetUp) return true
    if (this.customer.hasPlan) return true
    if (this.customer.subscription) return true
    else return this.completedSteps[step]
  }

  updateWords(newWords: number): void {
    this.words = newWords || 300 // only old word length we no longer have
    if (!this.stepComplete(this.Steps.WORDS)) {
      if (this.defaultQuantities[this.words]) {
        this.quantity = this.defaultQuantities[this.words]
      } else {
        // round down to lower key
        const availableWords: number[] = Object.keys(this.defaultQuantities).map(k => +k)
        availableWords.forEach(w => {
          if (w > this.words) {
            this.quantity = this.defaultQuantities[w]
            return
          }
        })
      }
    }
    this.quantityErrorMessage = this.getQuantityErrorMessage(this.quantity, this.words, this.account)
  }

  planName(): string {
    let name
    if (this.basic)
      name = 'Basic'
    else if (this.plusPhoto)
      name = '+Photo'
    else if (this.plusOptimize)
      name = '+Optimize'
    else if (this.verblioComplete)
      name = 'Complete'
    if (this.plusVideo)
      name += ', +Video'
    return name
  }

  contentTypesPopoverHtml(wordCount: number): string {
    const availableTypes = this.corePlanWordsSummaries[wordCount]
    const notAvailableTypes = this.notAvailableTypes[wordCount]
    return contentTypesLearnMoreHtml(wordCount, availableTypes, notAvailableTypes)
  }

  addonPopoverHtml(addon: string): string {
    return addonLearnMoreHtml(addon, this.photosPerPost())
  }

  popoverPlacement(index: number, total: number): string {
    if (this.isMobile) {
      return 'top'
    } else {
      if (index < total / 2) {
        return 'right'
      } else {
        return 'left'
      }
    }
  }

  selectPackage(addOnName: AddOnDescriptor, scroll: boolean = true, completeStep: boolean = true): void {
    if (this[addOnName]) return // Already selected
    this.basic = false
    Object.keys(packageCodes).forEach(packageName => this[packageName] = false)
    this[addOnName] = true
    if (completeStep) this.completeStep(this.Steps.PACKAGE, scroll)
  }

  toggleAddOn(addOnName: AddOnDescriptor, selected: boolean = !this[addOnName]): void {
    this[addOnName] = selected
  }

  monthlyPriceWithoutAddOns(options: IPriceOptions = { overrideBillingFrequency: null }): number {
    return this.pricePerPieceWithoutAddons(options) * this.quantity
  }

  monthlyPriceWithAddOns(options: IPriceOptions = { overrideBillingFrequency: null }): number {
    let totalMonthlyAddOnPrice = 0
    if (this.plusPhoto) totalMonthlyAddOnPrice += this.monthlyPriceForAddOn('plusPhoto', options)
    if (this.plusOptimize) totalMonthlyAddOnPrice += this.monthlyPriceForAddOn('plusOptimize', options)
    if (this.verblioComplete) totalMonthlyAddOnPrice += this.monthlyPriceForAddOn('verblioComplete', options)
    if (this.plusVideo) totalMonthlyAddOnPrice += this.monthlyPriceForAddOn('plusVideo', options)
    return this.monthlyPriceWithoutAddOns(options) + totalMonthlyAddOnPrice
  }

  finalPriceCents(): number {
    const finalPriceWithoutCoupons = this.monthlyPriceWithAddOns() * (this.billingFrequency === 'annually' ? 12 : 1)
    const coupons = this.billingFrequency === 'annually' ? [] : this.usableCoupons()
    const finalPrice = discountedCents(finalPriceWithoutCoupons, coupons)
    return max([finalPrice, 0])
  }

  annualSavings(): number {
    const monthlyPrice = this.monthlyPriceWithAddOns({overrideBillingFrequency: 'monthly'})
    const monthlyAnnualCost = discountedCents( monthlyPrice, this.usableCoupons() ) +
                              discountedCents( monthlyPrice * 11, this.recurringCoupons() )
    const annualCost = this.monthlyPriceWithAddOns({overrideBillingFrequency: 'annually'}) * 12

    return monthlyAnnualCost - annualCost
  }

  dollars(price: number): string {
    return prettyDollars(price)
  }

  cents(price: number): string {
    return prettyCents(price)
  }

  selectedPlan(overrideBillingFrequency?: BillingFrequency): Plan {
    const plans = this.plans || []
    const billingFrequency = overrideBillingFrequency || this.billingFrequency
    const currentPlanActive = get(this.customer, 'subscription.plan.active')
    const subscriptionState = get(this.customer, 'subscription.state')
    if (['active', 'paused'].includes(subscriptionState) && !currentPlanActive) {
      return plans.find(p => {
        return ((p.id >= 42 && p.id <= 71) || (p.id >= 112 && p.id <= 151)) &&
          p.words === this.words &&
          p.billingFrequency === billingFrequency
      })
    } else {
      return plans.find(p => {
        return p.active &&
          p.words === this.words &&
          p.billingFrequency === billingFrequency
      })
    }
  }

  planAddOnFor(addOnDescriptor: AddOnDescriptor, options: IPriceOptions = { overrideBillingFrequency: null }): PlanAddOn {
    const billingFrequency = options.overrideBillingFrequency || this.billingFrequency
    const plan = this.selectedPlan(billingFrequency)
    if (plan) {
      let addons = []
      if ([250, 300].includes(plan.words) && billingFrequency !== 'annually' && this.leaseLabsPilot()) {
        addons = plan.planAddOns.filter(ao => ao.name.includes('LeaseLabs'))
      } else if (!plan.active) {
        addons = plan.planAddOns.filter(ao => (ao.id >= 151 && ao.id <= 240) || (ao.id >= 644 && ao.id <= 833))
      } else {
        addons = plan.planAddOns.filter(ao => ao.active)
      }
      return addons.find(p => p.code === addOnCodes[addOnDescriptor])
    }
  }

  leaseLabsPilot(): boolean {
    if (this.customer) {
      const features = this.customer.enabledFeatures || []
      return features.includes('lease_labs_pilot')
    } else {
      return false
    }
  }

  selectedAddOnCodes(): string[] {
    let selectedAddOnCodes: string[] = []
    if (this.plusOptimize) selectedAddOnCodes.push(addOnCodes.plusOptimize)
    if (this.plusPhoto) selectedAddOnCodes.push(addOnCodes.plusPhoto)
    if (this.verblioComplete) selectedAddOnCodes.push(addOnCodes.verblioComplete)
    if (this.plusVideo) selectedAddOnCodes.push(addOnCodes.plusVideo)
    return selectedAddOnCodes
  }

  perPiecePriceForAddon(addon: AddOnDescriptor, options: IPriceOptions = { overrideBillingFrequency: null, useExtraPrice: false }): number {
    const planAddOn = this.planAddOnFor(addon, options)
    if (planAddOn) {
      const billingFrequency = options.overrideBillingFrequency || this.billingFrequency
      const addOnPrice = options.useExtraPrice ? planAddOn.extraPostPrice : planAddOn.price
      return billingFrequency !== 'annually' || options.useExtraPrice ? addOnPrice : addOnPrice / 12
    }
  }

  monthlyPriceForAddOn(addOnDescriptor: AddOnDescriptor, options: IPriceOptions = { overrideBillingFrequency: null, useExtraPrice: false }): number {
    return this.quantity * this.perPiecePriceForAddon(addOnDescriptor, options)
  }

  pricePerPieceWithoutAddons(options: IPriceOptions = { overrideBillingFrequency: null }): number {
    const billingFrequency = options.overrideBillingFrequency || this.billingFrequency
    const plan = this.selectedPlan(billingFrequency)
    if (plan) {
      return billingFrequency === 'annually' ? plan.price / 12 : plan.price
    }
  }

  // May want to use this in the future
  // addOnSummary(): string {
  //   let str = (this.plusOptimize ? '+Optimize!' : '') + (this.plusOptimize && (this.plusPhoto || this.verblioComplete) ? ', ' : '')
  //         + (this.plusPhoto ? '+Photo' : '') + (this.plusPhoto && this.verblioComplete ? ', ' : '')
  //         + (this.verblioComplete ? 'Verblio Complete' : '')
  //       || '';
  //   return str;
  // }

  /**
   * EXPIRED SUBSCRIPTIONS HANDLING
   * Look at previous subscription and set form to match
   */
  ngOnChanges(changeRecord: SimpleChanges): void {
    if (changeRecord.customer) {
      const customer = <Customer>changeRecord.customer.currentValue
      if (customer.subscription) this.updateFormWithCurrentSubscription(customer.subscription)
    }
  }

  updateFormWithCurrentSubscription(subscription: Subscription): void {
    const plan = subscription.plan
    if (plan) {
      this.billingFrequency = plan.billingFrequency
      this.words = plan.words === 250 ? 300 : plan.words
      this.customLength = !this.defaultWordLengths.includes(this.words)
      this.quantity = subscription.quantity
      const packageAddOnNames = this.filteredAddOns(subscription.planAddOns, packageCodes).map(a => addOnNameForCode[a.code] as AddOnDescriptor)
      const multiAddOnNames = this.filteredAddOns(subscription.planAddOns, multiCodes).map(a => addOnNameForCode[a.code] as AddOnDescriptor)

      if (this.paramsAddOn)
        this.selectPackage(this.paramsAddOn, false) // Don't select customer's addon, instead use params
      else if (packageAddOnNames.length > 0)
        this.selectPackage(packageAddOnNames[0], false)
      else
        this.selectPackage('basic', false)

      // Set all multiAddOns to false
      Object.keys(multiCodes).forEach(addOnName => this.toggleAddOn(addOnName as AddOnDescriptor, false))
      // Toggle the correct ones
      this.selectAddOns(multiAddOnNames)
    }
  }

  filteredAddOns(planAddOns: PlanAddOn[], codes: Partial<typeof addOnCodes>): PlanAddOn[] {
    return planAddOns.filter(a => values(codes).includes(a.code))
  }

  // Looks at current subscription add-ons and sets plusOptimize, plusPhoto, etc.. to true
  selectAddOns(planAddOnNames: AddOnDescriptor[]): void {
    planAddOnNames.forEach(addOnName => this.toggleAddOn(addOnName, true))
  }

  noPlanInfo(): boolean {
    return isEmpty(this.plans)
  }

  createPlanText(): string {
    if (this.noPlanInfo()) {
      return "Error: Please reload page"
    } else if (this.saving) {
      return "Saving"
    } else if (this.newCustomer) {
      let message : string
      if (this.adminImpersonating && this.account.createdByAdmin)
        return 'Invite customer'
      else if (this.account?.agency) {
        return `Complete ${(this.customer?.businessName || '')}`
      } else {
        return 'Continue to activate'
      }
    } else if (['active', 'paused'].includes(this.customer.state)) {
      return "Preview Changes"
    } else {
      return "Activate Subscription"
    }
  }

  agencySignup(): boolean {
    return !!this.account && !this.account.fullySetUp && this.account.agency
  }

  minNumberOfPosts: any = {
    300: 3, 350: 3, 400: 3, 450: 3, 500: 3, 550: 3,
    600: 2, 650: 2, 700: 2, 750: 2, 800: 2, 850: 2, 900: 2, 950: 2
  };

  getQuantityErrorMessage(numberOfPosts: number, words: number, account: Account): string {
    if (
      (account && !account.fullySetUp && !account.agency && numberOfPosts < this.minNumberOfPosts[words]) ||
      (account && !account.agency && account.customersCount === 1 && numberOfPosts < this.minNumberOfPosts[words])
    ) {
      return `We're sorry, you must select ${this.minNumberOfPosts[words]} or more ${words} word posts for a valid subscription.`
    }
    else return null
  }

  hasCoupons(): boolean {
    return this.usableCoupons().length > 0
  }

  recurringCoupons(): Coupon[] {
    return this.usableCoupons().filter(c => c.duration === 'forever')
  }

  usableCoupons(): Coupon[] {
    if (this.customer && this.account) {
      return availableCouponApplications(this.customer, this.account).filter(ca => ca.usable).map(ca => ca.coupon)
    } else {
      return []
    }
  }

  photosPerPost(): number {
    const p = this.selectedPlan()
    if (p) {
      if (p.words >= 2000) return 3
      else if (p.words >= 800) return 2
      return 1
    } else {
      return 1
    }
  }

  photosPerPostInWords(): string {
    const numbers = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine']
    return numbers[this.photosPerPost()]
  }

  onRecentOldPlan(): boolean {
    const plan: Plan = get(this.customer, ['subscription', 'plan'])
    return plan ? !plan.active && plan.code.includes('flexi') : false
  }

  simplePriceFormat(price: number, overrideBillingFrequency: BillingFrequency): string {
    const billingFrequency = overrideBillingFrequency ? overrideBillingFrequency : this.billingFrequency
    const dollars = this.dollars(price * (billingFrequency === 'annually' ? 12 : 1))
    const cents = this.cents(price * (billingFrequency === 'annually' ? 12 : 1))
    return `$${dollars}` + (cents ? `.${cents}` : '')
  }

  completeStep(step: number, scroll: boolean = true): void {
    this.completedSteps[step] = true
    if (scroll) {
      setTimeout(() => {
        const node = document?.getElementById(`step-${step + 1}`) as Element
        if (node) scrollIntoViewIfNeeded(node, { easing: 'easeIn', duration: 220, offset: { bottom: 100 } })
      }, 0)
    }
  }

  saveSelections(): void {
    if (!this.customer) {
      localStorage.setItem('pricing', JSON.stringify(
        pick(this, ['words', 'quantity', 'basic', 'plusPhoto', 'plusOptimize', 'plusVideo', 'verblioComplete', 'billingFrequency'])
      ))
    }
  }

  loadPreviousSelections(): void {
    const selectionsJson = localStorage.getItem('pricing')
    if (selectionsJson) {
      Object.assign(this, JSON.parse(selectionsJson))
      this.steps = false // expand everything
    }
  }

  get www(): boolean {
    return !this.customer
  }

  get videoSavings(): string {
    const dollars = this.dollars(
      this.perPiecePriceForAddon('plusVideo', {useExtraPrice: true}) -
      this.perPiecePriceForAddon('plusVideo'))

    const cents = this.cents(
      this.perPiecePriceForAddon('plusVideo', {useExtraPrice: true}) -
      this.perPiecePriceForAddon('plusVideo'))

    let savings = '$' + dollars
    if (cents) savings += `.${cents}`
    return savings
  }
}

const packageCodes = {
  plusOptimize: 'option-a',
  plusPhoto: 'option-b',
  verblioComplete: 'option-c',
}

const multiCodes = {
  plusVideo: 'option-h',
}

const addOnCodes = {
  ...packageCodes,
  ...multiCodes,
}

type AddOnDescriptor = 'basic' | 'plusOptimize' | 'plusPhoto' | 'verblioComplete' | 'plusVideo';

const addOnNameForCode = {
  'option-a': 'plusOptimize',
  'option-b': 'plusPhoto',
  'option-c': 'verblioComplete',
  'option-h': 'plusVideo'
}

interface IPriceOptions {
  overrideBillingFrequency?: BillingFrequency;
  useExtraPrice?: boolean
}
